export const NotificationEvents = {
  // Core notification events
  newNotification: (data = {}) => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'new_notification', 
        notification: data,
        incrementCount: true,
        ...data 
      }
    }));
  },

  notificationRead: (data = {}) => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'notification_read', 
        notificationId: data.id,
        decrementCount: true,
        ...data 
      }
    }));
  },

  allNotificationsRead: (markedCount = 0) => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'all_read', 
        count: 0,
        markedCount: markedCount
      }
    }));
  },

  notificationDeleted: (data = {}) => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'notification_deleted', 
        notificationId: data.id,
        decrementCount: data.wasUnread || false,
        ...data 
      }
    }));
  },

  updateCount: (count) => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'count_update',
        count: count 
      }
    }));
  },

  // Ticket-specific notification events
  ticketAssigned: (ticketData) => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'ticket_assigned', 
        ticket: ticketData,
        incrementCount: true,
        notification: {
          type: 'App\\Notifications\\TicketAssignedNotification',
          data: {
            ticket_id: ticketData.id,
            ticket_title: ticketData.title,
            message: `Ticket "${ticketData.title}" has been assigned to you`
          }
        }
      }
    }));
  },

  ticketEscalated: (ticketData, reason = '') => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'ticket_escalated', 
        ticket: ticketData,
        reason: reason,
        incrementCount: true,
        notification: {
          type: 'App\\Notifications\\TicketEscalatedNotification',
          data: {
            ticket_id: ticketData.id,
            ticket_title: ticketData.title,
            message: `Ticket "${ticketData.title}" has been escalated to you`,
            escalation_reason: reason
          }
        }
      }
    }));
  },

  ticketForwarded: (ticketData, reason = '') => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'ticket_forwarded', 
        ticket: ticketData,
        reason: reason,
        incrementCount: true,
        notification: {
          type: 'App\\Notifications\\TicketForwardedNotification',
          data: {
            ticket_id: ticketData.id,
            ticket_title: ticketData.title,
            message: `Ticket "${ticketData.title}" has been forwarded to you`,
            forward_reason: reason
          }
        }
      }
    }));
  },

  ticketCreated: (ticketData) => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'ticket_created', 
        ticket: ticketData,
        incrementCount: true,
        notification: {
          type: 'App\\Notifications\\TicketCreatedNotification',
          data: {
            ticket_id: ticketData.id,
            ticket_title: ticketData.title,
            message: `New ticket created: "${ticketData.title}"`
          }
        }
      }
    }));
  },

  ticketResolved: (ticketData) => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'ticket_resolved', 
        ticket: ticketData,
        incrementCount: true,
        notification: {
          type: 'App\\Notifications\\TicketResolvedNotification',
          data: {
            ticket_id: ticketData.id,
            ticket_title: ticketData.title,
            message: `Ticket "${ticketData.title}" has been resolved`
          }
        }
      }
    }));
  },

  ticketCommented: (ticketData, comment) => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'ticket_commented', 
        ticket: ticketData,
        comment: comment,
        incrementCount: true,
        notification: {
          type: 'App\\Notifications\\TicketCommentedNotification',
          data: {
            ticket_id: ticketData.id,
            ticket_title: ticketData.title,
            message: `New comment on ticket: "${ticketData.title}"`
          }
        }
      }
    }));
  },

  // Real-time WebSocket notification handler
  handleRealTimeNotification: (notification) => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'real_time',
        notification: notification,
        incrementCount: !notification.read_at,
        notificationType: notification.type
      }
    }));
  },

  // WebSocket connection status events
  connectionEstablished: () => {
    window.dispatchEvent(new CustomEvent('notificationConnection', {
      detail: { 
        type: 'connected',
        status: 'connected',
        timestamp: new Date().toISOString()
      }
    }));
  },

  connectionLost: () => {
    window.dispatchEvent(new CustomEvent('notificationConnection', {
      detail: { 
        type: 'disconnected',
        status: 'disconnected',
        timestamp: new Date().toISOString()
      }
    }));
  },

  connectionReconnecting: () => {
    window.dispatchEvent(new CustomEvent('notificationConnection', {
      detail: { 
        type: 'reconnecting',
        status: 'reconnecting',
        timestamp: new Date().toISOString()
      }
    }));
  },

  // Error handling
  error: (error, context = '') => {
    window.dispatchEvent(new CustomEvent('notificationError', {
      detail: { 
        type: 'error',
        error: error,
        context: context,
        timestamp: new Date().toISOString()
      }
    }));
  },

  // Batch operations
  bulkMarkAsRead: (notificationIds) => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'bulk_read',
        notificationIds: notificationIds,
        count: 0 // Assume all notifications are now read
      }
    }));
  },

  bulkDelete: (notificationIds, unreadCount = 0) => {
    window.dispatchEvent(new CustomEvent('notificationUpdate', {
      detail: { 
        type: 'bulk_delete',
        notificationIds: notificationIds,
        decrementCount: unreadCount
      }
    }));
  },

  // Custom event for notification list refresh
  refreshNotifications: () => {
    window.dispatchEvent(new CustomEvent('notificationRefresh', {
      detail: { 
        type: 'refresh_requested',
        timestamp: new Date().toISOString()
      }
    }));
  },

  // Preference updates
  preferencesUpdated: (preferences) => {
    window.dispatchEvent(new CustomEvent('notificationPreferences', {
      detail: { 
        type: 'preferences_updated',
        preferences: preferences,
        timestamp: new Date().toISOString()
      }
    }));
  },

  // Helper method to dispatch generic custom events
  dispatch: (eventType, eventData = {}) => {
    window.dispatchEvent(new CustomEvent(eventType, {
      detail: {
        timestamp: new Date().toISOString(),
        ...eventData
      }
    }));
  }
};

// Export individual event creators for convenient importing
export const {
  newNotification,
  notificationRead,
  allNotificationsRead,
  notificationDeleted,
  updateCount,
  ticketAssigned,
  ticketEscalated,
  ticketForwarded,
  ticketCreated,
  ticketResolved,
  ticketCommented,
  handleRealTimeNotification,
  connectionEstablished,
  connectionLost,
  connectionReconnecting,
  error,
  bulkMarkAsRead,
  bulkDelete,
  refreshNotifications,
  preferencesUpdated,
  dispatch
} = NotificationEvents;

export default NotificationEvents;